<template>
  <div class="frame-wrap" :style="wrapStyle">
    <!-- Decorative ring (behind avatar) -->
    <div class="plan-ring" :class="`ring--${planKey || 'standard'}`"></div>

    <!-- Avatar -->
    <v-avatar :size="size" class="avatar">
      <v-img :src="src" :alt="alt" />
    </v-avatar>

    <!-- Small caption under the frame (optional) -->
    <div v-if="planKey && planKey != 'standard'" class="caption" :class="`caption--${planKey}`">
      <v-icon size="14" class="mr-1">{{ badge.icon }}</v-icon>
      <span>{{ badge.label }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PlanBadgeAvatar',
  props: {
    src: { type: String, required: true },
    alt: { type: String, default: 'Profile photo' },
    size: { type: [Number, String], default: 140 },
    /** 'standard' | 'enhanced' | 'premium' | null */
    plan: { type: String, default: null },
  },
  computed: {
    sizeNum () {
      const s = this.size
      if (typeof s === 'number') return s
      const n = parseFloat(String(s))
      return Number.isNaN(n) ? 140 : n
    },
    wrapStyle () {
      // Add room for the outer ring + caption
      const ring = 10  // ring thickness in px
      const pad = ring + 10
      return {
        width: `${this.sizeNum + pad * 2}px`,
        height: `${this.sizeNum + pad * 2 + 22}px`, // extra for caption
      }
    },
    planKey () {
      if (!this.plan) return null
      const k = String(this.plan).toLowerCase()
      return ['enhanced', 'premium'].includes(k) ? k : 'standard'
    },
    badge () {
      switch (this.planKey) {
        case 'premium':  return { label: 'Premium',  icon: 'mdi-crown' }
        case 'enhanced': return { label: 'Enhanced', icon: 'mdi-star-circle' }
        default:         return { label: 'Standard', icon: '' }
      }
    },
  },
}
</script>

<style scoped>
.frame-wrap {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
}

/* Avatar is centered inside the ring container */
.avatar {
  position: relative;
  z-index: 2;
  margin-top: 20px; /* matches ring thickness */
}

/* PLAN RING
   Use a conic-gradient for a nice premium feel; falls back to solid if unsupported */
.plan-ring {
  position: absolute;
  inset: 0 0 22px 0; /* leave space at bottom for caption */
  display: grid;
  place-items: center;
  z-index: 1;
  pointer-events: none;
}
.plan-ring::before {
  content: "";
  width: calc(100% - 22px);
  height: calc(100% - 22px); /* reserve space for caption visually */
  border-radius: 50%;
  box-sizing: border-box;
  padding: 10px; /* ring thickness */
  background:
    radial-gradient(transparent 64%, rgba(0,0,0,0) 64%) padding-box,
    var(--ring-gradient, linear-gradient(#bbb,#bbb)) border-box;
  border: 0 solid transparent; /* required for the layered background trick */
  -webkit-mask:
    radial-gradient(farthest-side, transparent calc(100% - 10px), #000 calc(100% - 9.5px));
  mask:
    radial-gradient(farthest-side, transparent calc(100% - 10px), #000 calc(100% - 9.5px));
  filter: drop-shadow(0 6px 12px rgba(0,0,0,.15));
}

/* Ring themes */
.ring--standard { --ring-gradient: linear-gradient(#c9c9c9, #b5b5b5); }
.ring--enhanced {
  --ring-gradient:
    conic-gradient(
      from 220deg,
      #ffcc55 0deg, #ffb300 120deg, #ff9f00 240deg, #ffd166 360deg
    );
}
.ring--premium {
  --ring-gradient:
    conic-gradient(
      from 200deg,
      #8b5cf6 0deg, #8b5cf6 120deg, #6a00ff 240deg, #8b5cf6 360deg
    );
}

/* Corner “brackets” acting as part of the frame (outside avatar, subtle) */
.corner {
  position: absolute;
  width: 24%;
  height: 24%;
  pointer-events: none;
  z-index: 3;
}
.corner.tl { top: 3px; left: 3px; }
.corner.br { bottom: 24px; right: 3px; } /* leave bottom room for caption */

.corner::before,
.corner::after {
  content: "";
  position: absolute;
  background: currentColor;
  opacity: .9;
}

/* L-shape: vertical + horizontal bars */
.corner.tl::before { /* vertical */
  left: 0; top: 0; width: 3px; height: 60%;
}
.corner.tl::after { /* horizontal */
  left: 0; top: 0; width: 60%; height: 3px;
}
.corner.br::before {
  right: 0; bottom: 0; width: 3px; height: 60%;
}
.corner.br::after {
  right: 0; bottom: 0; width: 60%; height: 3px;
}

/* Corner colors per plan */
.corner--enhanced { color: #ffb300; }
.corner--premium  { color: #6a00ff; }

/* Caption below avatar (small, doesn’t cover image) */
.caption {
  position: absolute;
  bottom: 0;
  transform: translateY(2px);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .2px;
  text-transform: uppercase;
  color: #fff;
  box-shadow: 0 6px 14px rgba(0,0,0,.18);
}
.caption--enhanced { background: #ffb300; }
.caption--premium  { background: #6a00ff; }

/* Small screens: tighten spacing a tad */
@media (max-width: 480px) {
  .caption { font-size: 11px; padding: 3px 8px; }
}
</style>
